import { useContext, useEffect } from 'react';
import api from 'api';
import { Listing } from 'models/Listing';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import {
  ListingVisitedEventProps,
  WitnessedListingVisitedEventProps,
} from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  getApiAutorization,
  getCurrentDateInMiliSeconds,
  getElapsedTimeInSecs,
} from 'utils';
import { getIsBuySide } from 'router/subrouters/DataRoom/utils';
import credentialsService from 'services/credentialsService';

type UseMixpanelEventsProps = {
  listing?: Listing;
  listingId: string;
  dealRoomId: string;
  isListingOwner: boolean;
};

export default function useMixpanelEvents(props: UseMixpanelEventsProps) {
  const { listing, listingId, dealRoomId, isListingOwner } = props;

  const { isSuperAdmin, currentUser } = useContext(CurrentUserContext);

  const triggererId = currentUser?._id;

  const listingName = listing?.listingTitle;
  const userId = listing?.userId;

  const userSide = triggererId
    ? isSuperAdmin || isListingOwner
      ? Q_AND_A_SIDES.SELL
      : Q_AND_A_SIDES.BUY
    : null;

  const getMixpanelProps = useGetMixpanelProps();

  useEffect(() => {
    if (!listingId) return;

    const startedAt = getCurrentDateInMiliSeconds();
    const token = credentialsService.token;

    return () => {
      const timeOnPageInSecs = getElapsedTimeInSecs(startedAt);

      mixpanelService.listingVisited(
        getMixpanelProps({
          listingName,
          dealId: dealRoomId,
          userSide,
          timeOnPageInSecs,
        }) as ListingVisitedEventProps,
      );

      if (getIsBuySide(userSide)) {
        api.mixpanel.witnessedListingVisited(
          getMixpanelProps({
            userId,
            triggererId,
            listingName,
            dealId: dealRoomId,
            timeOnPageInSecs,
            isWitnessedEvent: true,
          }) as WitnessedListingVisitedEventProps,
          { headers: { Authorization: getApiAutorization(token) } },
        );
      }
    };
  }, [
    dealRoomId,
    listingName,
    listingId,
    triggererId,
    userSide,
    userId,
    getMixpanelProps,
  ]);
}
